import React, { useState, useEffect, useCallback, useMemo } from 'react';
import BlogCardExpand from './BlogCardExpand';
import { FaRegFileImage } from 'react-icons/fa';

import { requestPublics } from '../services/requests';
import { normalizeBlogImageUrl } from '../utils/blogImageUrl';

const TAG_COLORS = [
	'#69314C',
	'#6E3630',
	'#28456C',
	'#5A5A5A',
	'#2B593F',
	'#492F64',
	'#414F44',
];

const BlogShortcut = () => {
	const [blogCardStatus, setBlogCardStatus] = useState(false);
	const [showCards, setShowCards] = useState(false);
	const [currentEvent, setCurrentEvent] = useState({});
	const [notes, setNotes] = useState([]);

	const [imageErrors, setImageErrors] = useState({});

	const traduzirData = useCallback((dataISO) => {
		const meses = [
			'janeiro',
			'fevereiro',
			'março',
			'abril',
			'maio',
			'junho',
			'julho',
			'agosto',
			'setembro',
			'outubro',
			'novembro',
			'dezembro',
		];
		const [ano, mes, dia] = dataISO.split('-');
		return `${parseInt(dia)} de ${meses[parseInt(mes) - 1]} de ${ano}`;
	}, []);

	const fetchPublics = useCallback(async () => {
		const response = await requestPublics();

		if (!response.error) {
			const updatedNotes = window.location.href.includes('blog')
				? response
				: response.slice(0, 3);

			setNotes(updatedNotes);
			setImageErrors({});

			const ultimoElemento = window.location.pathname
				.split('/')
				.filter(Boolean)
				.pop();
			if (ultimoElemento !== 'blog') {
				const result = response.filter(
					(item) => item.id === Number(ultimoElemento),
				);
				if (result.length > 0) {
					setCurrentEvent(result[0]);
					setBlogCardStatus(true);
				}
			}
		}
	}, []);

	const handleImageError = useCallback((key) => {
		setImageErrors((prev) => ({
			...prev,
			[key]: true,
		}));
	}, []);

	const getImageKey = useCallback((evento, index) => {
		return evento?.id ?? `${evento?.titulo || 'evento'}-${index}`;
	}, []);

	const handleDataFromChild = useCallback((data) => {
		if (data === false) {
			setCurrentEvent({});
		}
		setBlogCardStatus(data);
	}, []);

	const getTagColor = useCallback((index) => {
		return TAG_COLORS[index % TAG_COLORS.length];
	}, []);

	const openBlogCard = useCallback(() => {
		setBlogCardStatus((prev) => !prev);
	}, []);

	useEffect(() => {
		fetchPublics();
		setShowCards(false);
		const timer = setTimeout(() => {
			setShowCards(true);
		}, 50);

		return () => {
			clearTimeout(timer);
		};
	}, [fetchPublics]);

	const notesWithImages = useMemo(
		() =>
			notes.map((evento, index) => ({
				evento,
				index,
				imageUrl: normalizeBlogImageUrl(evento?.imagem),
				imageKey: getImageKey(evento, index),
			})),
		[notes, getImageKey],
	);

	const BlogCardList = useMemo(() => {
		if (notes.length === 0) {
			return (
				<div className='loader'>
					<div
						className='flex justify-center h-[1200px] content-center align-bottom items-baseline w-full my-20'
						role='status'>
						<svg
							aria-hidden='true'
							className='w-8 h-8 text-gray-200 animate-spin mt-[100px] fill-blue-600'
							viewBox='0 0 100 101'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'>
							<path
								d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
								fill='currentColor'
							/>
							<path
								d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
								fill='currentFill'
							/>
						</svg>
					</div>
				</div>
			);
		}

		return (
			<>
				{notesWithImages.map(({ evento, index, imageUrl, imageKey }) => {
					const shouldShowFallback =
						!imageUrl || imageErrors[imageKey];

					return (
						<button
							key={index + 'a12'}
							onClick={() => {
								if (evento !== currentEvent) {
									setCurrentEvent(evento);
									openBlogCard();
								}
							}}
							className={`flex flex-row transform transition-all duration-1000 ease-out ${
								showCards
									? 'opacity-100 translate-y-0'
									: 'opacity-0 translate-y-10'
							}`}>
							<div className='bg-[#F5F5F5] relative justify-center mx-3 w-[340px] h-[320px] rounded-md mb-10 shadow-md hover:scale-105 transition duration-500 shadow-[#c9c4c4]'>
								<div className='rounded-t-md w-[340px] h-[150px] bg-[#E3E3E3] overflow-hidden'>
									{shouldShowFallback ? (
										<FaRegFileImage
											className='mx-auto h-full  text-[#d8d8d8] text-[50px]'
											size={50}
										/>
									) : (
										<img
											className='w-full h-full object-cover'
											alt='foto'
											src={imageUrl}
											onError={() =>
												handleImageError(imageKey)
											}
										/>
									)}
								</div>
								<div className='py-2 px-4'>
									<p className='text-[#757575] text-[15px] font-semibold overflow-hidden mb-3 text-start break-words line-clamp-2 fade'>
										{evento.titulo}
									</p>
									<div className='flex flex-wrap gap-2 text-[12px] relative max-h-[50px] overflow-hidden'>
										{evento.tags
											.split(',')
											.map((topico, index) => (
												<div
													key={index}
													className='px-1.5 rounded-[4px] font-normal text-[#dddddd] flex-shrink-0'
													style={{
														backgroundColor:
															getTagColor(
																index,
															),
													}}>
													{topico}
												</div>
											))}
									</div>
									<div className='absolute bottom-0'>
										<p className='text-[#757575] font-normal text-start my-3'>
											{traduzirData(
												evento.data_publicacao,
											)}
										</p>
									</div>
								</div>
							</div>
						</button>
					);
				})}
			</>
		);
	}, [
		notes,
		notesWithImages,
		currentEvent,
		showCards,
		getTagColor,
		openBlogCard,
		imageErrors,
		handleImageError,
	]);

	return (
		<div className='flex flex-wrap gap-4 justify-center mt-5'>
			{BlogCardList}
			{blogCardStatus && currentEvent && (
				<BlogCardExpand
					sendDataToParent={handleDataFromChild}
					currentEvent={currentEvent}
				/>
			)}
		</div>
	);
};

export default BlogShortcut;
